import React, { useState, useEffect, useRef, useMemo } from "react";
import PropTypes from "prop-types";

import { InputDropDown, INPUTDROPDOWN_TYPE } from "widgets/inputdropdown";
import { Menu, MenuItem } from "widgets/menu";
import { formatNumber, parseNumber } from "widgets/inputs";
import { DocumentSuggestions, resolveCurrencyType } from "widgets/others";
import { Scrollbar, SCROLLBAR_THICKNESS } from "widgets/scrollbar";

import { Currency as CurrencyReadOnly } from "../../renderers/currency";
import { Base } from "../base";

import styles from "./currency.css";

function isNumber(value) {
  return !isNaN(value);
}

export function Currency(props) {
  const { column, value, pos, suggestionList } = props;
  const baseRef = useRef(null);
  const inputRef = useRef(null);
  const suggestionRef = useRef(null);
  const [currentValue, setCurrentValue] = useState(value);
  const [readOnly, setReadOnly] = useState(true);
  const dropdownOpened = useRef(false);

  const decimalPoint = useMemo(
    function getDecimalPoint() {
      return (
        column.Decimalpoint ||
        (column.modelConfig && column.modelConfig.Decimalpoint)
      );
    },
    [column]
  );
  const currentNumberValue = useMemo(
    function updateCurrentNumberValue() {
      if (currentValue) {
        let number = formatNumber(
          currentValue.split(" ")[0],
          null,
          currentValue.split(" ")[1]
        );
        return number?.split(" ")[1];
      }
      return null;
    },
    [currentValue]
  );
  const currentCurrencyType = useMemo(
    function updateCurrentCurrencyType() {
      if (currentValue) {
        return currentValue.split(" ")[1];
      }
      return column?.CurrencyTypes?.[0];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentValue]
  );
  const showCurrencyTypes = useMemo(
    function showCurrencyTypes() {
      return !column?.CurrencyTypes.find((el) => el === currentCurrencyType);
    },
    [column, currentCurrencyType]
  );
  const updateChanges = (from) => {
    if (currentNumberValue) {
      let parsed = parseNumber(currentNumberValue);
      let finValue = formatNumber(parsed, decimalPoint);
      setCurrentValue(`${finValue} ${currentCurrencyType}`);
      // let showNum = finValue && finValue.split(" ") && finValue.split(" ")[1];
      finValue = parseNumber(finValue);
      if (isNumber(finValue)) {
        baseRef.current.dataChanged(`${finValue} ${currentCurrencyType}`);
      } else {
        baseRef.current.dataChanged("");
      }
    } else {
      setCurrentValue("");
      baseRef.current.dataChanged("");
    }
    baseRef.current.dataCompleted();
  };
  useEffect(
    function onValueChange() {
      baseRef.current.stopEditing();
      setCurrentValue(value || "");
      setReadOnly(true);
    },
    [value, pos]
  );

  return (
    <Base
      {...props}
      ref={baseRef}
      onEditing={(value) => {
        setTimeout(() => {
          if (value) {
            setCurrentValue(`${value} ${currentCurrencyType}`);
            if (isNumber(value))
              baseRef.current.dataChanged(`${value} ${currentCurrencyType}`);
          }
          setTimeout(() => {
            inputRef.current?.focus();
          });
          setReadOnly(false);
          suggestionRef.current?.show();
        });
      }}
      type="currency"
      doubleClickToEdit={true}
    >
      {(readOnly || column.readOnly) && (
        <CurrencyReadOnly column={column} value={value} />
      )}
      {!readOnly && !column.readOnly && (
        <InputDropDown
          value={currentNumberValue}
          selectedValue={currentCurrencyType}
          type={INPUTDROPDOWN_TYPE.MINIMAL}
          className={styles.currency}
          ref={inputRef}
          onChange={(value) => {
            setCurrentValue(`${parseNumber(value)} ${currentCurrencyType}`);
            // if (isNumber(value))
            baseRef.current.dataChanged(
              `${parseNumber(value)} ${currentCurrencyType}`
            );
          }}
          onEnter={() => {
            updateChanges("enter");
          }}
          onBlur={() => {
            // console.log("CURRENT VAL BLUR", currentValue);
            if (!dropdownOpened.current) {
              updateChanges();
            }
          }}
          onOpen={() => {
            dropdownOpened.current = true;
          }}
          onClose={() => {
            dropdownOpened.current = false;
          }}
          showList={
            showCurrencyTypes ||
            (column.CurrencyTypes && column.CurrencyTypes.length > 1)
          }
          listRenderer={({ closePopup }) => {
            return (
              <Scrollbar
                style={{ maxHeight: "25.6rem" }}
                thickness={SCROLLBAR_THICKNESS.MEDIUM}
              >
                <Menu
                  onChange={(value) => {
                    setCurrentValue(
                      `${parseNumber(currentNumberValue) || ""} ${value.key}`
                    );
                    baseRef.current.dataChanged(
                      `${parseNumber(currentNumberValue) || ""} ${value.key}`
                    );
                    closePopup();
                    inputRef.current.focus();
                  }}
                >
                  {column.CurrencyTypes?.map((type) => {
                    return (
                      <MenuItem key={type} onClick={(e) => {}}>
                        {type}
                      </MenuItem>
                    );
                  })}
                </Menu>
              </Scrollbar>
            );
          }}
          onKeyDown={(e) => {
            if (e.key === "Tab") {
              baseRef.current.dataCompleted();
              e.preventDefault();
              if (e.shiftKey) {
                baseRef.current.movePrevious();
              } else {
                baseRef.current.moveNext();
              }
            }
            if (e.key === "Enter") {
              baseRef.current.dataCompleted();
              e.preventDefault();
              baseRef.current.moveNextLine();
            }
            suggestionRef.current?.onKeyDown(e, column, false);
            e.stopPropagation();
          }}
        />
      )}
      {!readOnly && !column.readOnly && suggestionList?.length > 0 && (
        <DocumentSuggestions
          ref={suggestionRef}
          onSelect={(value, meta) => {
            value = `${parseNumber(value)} ${resolveCurrencyType(
              column.CurrencyTypes,
              meta
            )}`;
            setCurrentValue(value);
            baseRef.current.dataChanged(value);
            baseRef.current.dataCompleted();
          }}
          suggestionList={suggestionList}
        />
      )}
      {/* <CurrencyWidget
        value={currentValue || ""}
        ref={inputRef}
        className={`${style.input} base`}
        currencyTypes={column.CurrencyTypes || []}
        onChange={(value) => {
          setCurrentValue(value);
          baseRef.current.dataChanged(value);
          baseRef.current.dataCompleted();
        }}
      /> */}
    </Base>
  );
}

Currency.propTypes = {
  value: PropTypes.any,
  column: PropTypes.any,
  suggestionList: PropTypes.array,
  pos: PropTypes.any
};
